// contexts/AuthContext.js
import { announceDeliberateLogout } from "../../utils/deliberateLogoutPresence";
import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { createSessionMessageApi } from '../../utils/actionableNotifications';
import { setOnUnauthorized } from "../axiosSetup";
import { dismissAllNotifications } from "../../utils/notificationRegistry";
import { resetPaymentReasonCodes } from "../payments-v2/viewmodels/usePaymentReasonCodes";
import { clearUnscopedSessionStorage, resetSessionData } from '../../utils/sessionLifecycle';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Read stored auth synchronously so the very first render already knows the
// user is logged in — initializing in a useEffect paints the login page for
// one frame on every refresh (the "login flash").
function readStoredAuth() {
    try {
        const storedAuth = localStorage.getItem('auth');
        if (storedAuth) {
            const parsedAuth = JSON.parse(storedAuth);
            if (parsedAuth?.token) {
                // Keep the flat token key in sync — every API call reads it
                localStorage.setItem('accessToken', parsedAuth.token);
                return {
                    id: parsedAuth.id,
                    token: parsedAuth.token,
                    user: parsedAuth.user,
                    session_id: parsedAuth.session_id ?? null,
                    device_id: parsedAuth.device_id ?? null,
                    client_device_id: parsedAuth.client_device_id ?? null,
                    isAuthenticated: true
                };
            }
        }
    } catch (error) {
        console.error('Error initializing auth:', error);
        localStorage.removeItem('auth');
    }
    localStorage.removeItem('accessToken');
    return { id: null, token: null, user: null, session_id: null, device_id: null, client_device_id: null, isAuthenticated: false };
}

// All the QuerysetV4/chat-page UI state persisted under this prefix —
// sidebar chat filter picks (useWatiChatList), selected agents, the query
// search term, the last-open chat — tied to whoever's signed in, so none of
// it should survive into the next person's session on a shared machine.
function endSessionData() {
    resetSessionData();
    resetPaymentReasonCodes();
    clearUnscopedSessionStorage();
    dismissAllNotifications();
}

export const AuthProvider = ({ children }) => {
    const [authState, setAuthState] = useState(readStoredAuth);
    const [sessionVersion, setSessionVersion] = useState(0);
    const logoutFlight = useRef(null);
    const currentAuth = useRef(authState); currentAuth.current = authState;

    // Auto-logout when the backend rejects the token ("not logged in") —
    // registered once; the axios response interceptor calls this.
    useEffect(() => {
        setOnUnauthorized(() => {
            // Skip if already logged out (e.g. race between parallel calls)
            if (!localStorage.getItem('auth')) return;
            currentAuth.current = { id: null, token: null };
            endSessionData();
            setSessionVersion(version => version + 1);
            setAuthState({ id: null, token: null, user: null, session_id: null, device_id: null, client_device_id: null, isAuthenticated: false });
            localStorage.removeItem('auth');
            localStorage.removeItem('accessToken');
            createSessionMessageApi().warning("Your session has expired — please sign in again.", 6);
        });
        return () => setOnUnauthorized(null);
    }, []);

    // Login function
    const login = (userData) => {
        // Direct A -> B login has the same boundary as logout, before B mounts.
        if (authState.id !== userData.id || authState.token !== userData.token) {
            if (authState.isAuthenticated) endSessionData();
            else { resetSessionData(); clearUnscopedSessionStorage(); }
            setSessionVersion(version => version + 1);
        }
        const authData = {
            id: userData.id,
            token: userData.token,
            user: userData.user,
            session_id: userData.session_id ?? null,
            device_id: userData.device_id ?? null,
            client_device_id: userData.client_device_id ?? null,
            isAuthenticated: true
        };

        currentAuth.current = authData;
        setAuthState(authData);
        localStorage.setItem('auth', JSON.stringify(authData));
        if (userData.token) localStorage.setItem('accessToken', userData.token);
        else localStorage.removeItem('accessToken');
    };

    // Only this explicit user action sends the presence hint; expiry skips it.
    const logout = () => {
        const owner = currentAuth.current;
        if (logoutFlight.current?.owner.id === owner.id && logoutFlight.current?.owner.token === owner.token) return logoutFlight.current.promise;
        const complete = () => {
            if (currentAuth.current.id !== owner.id || currentAuth.current.token !== owner.token) return;
            currentAuth.current = { id: null, token: null };
            endSessionData();
            setSessionVersion(version => version + 1);
            setAuthState({ id: null, token: null, user: null, session_id: null, device_id: null, client_device_id: null, isAuthenticated: false });
            localStorage.removeItem('auth');
            localStorage.removeItem('accessToken');
        };
        const pending = announceDeliberateLogout();
        if (!pending) { complete(); return Promise.resolve(); }
        const flight = { owner, promise: null };
        flight.promise = pending.then(complete).finally(() => { if (logoutFlight.current === flight) logoutFlight.current = null; });
        logoutFlight.current = flight;
        return flight.promise;
    };

    // Signing out/switching in one tab invalidates all other tabs too. Storage
    // has already changed, so guards also stop work before this event is handled.
    useEffect(() => {
        const changed = event => {
            if (event.key !== 'auth' && event.key !== null) return;
            const next = readStoredAuth(), current = currentAuth.current;
            if (current.id !== next.id || current.token !== next.token || current.session_id !== next.session_id) {
                endSessionData(); setSessionVersion(version => version + 1);
            }
            currentAuth.current = next;
            setAuthState(next);
        };
        window.addEventListener('storage', changed);
        return () => window.removeEventListener('storage', changed);
    }, []);

    // Update user data
    const updateUser = (userData) => {
        if (currentAuth.current.id !== authState.id || currentAuth.current.token !== authState.token) return;
        const updatedAuth = {
            ...authState,
            user: { ...authState.user, ...userData }
        };
        currentAuth.current = updatedAuth;
        setAuthState(updatedAuth);
        localStorage.setItem('auth', JSON.stringify(updatedAuth));
    };

    const value = {
        authState,
        login,
        logout,
        updateUser,
        isAuthenticated: authState.isAuthenticated
    };

    return (
        <AuthContext.Provider value={value}>
            <React.Fragment key={`${authState.id}:${sessionVersion}`}>{children}</React.Fragment>
        </AuthContext.Provider>
    );
};

// Keep the default export for backward compatibility
export { AuthContext };
